import { Loader2 } from "lucide-react"
import type { ReactNode } from "react"
import { useEffect, useId, useRef, useState } from "react"
import { JobElapsedTimer, jobStatusLabel, statusDetailLabel } from "@/components/jobs/job-elapsed-timer"
import { registerGenerationMotion, type GenerationMotionHandle } from "@/lib/generation-motion/engine"
import { jobStartedAt } from "@/lib/generation-started-at"
import { cn } from "@/lib/utils"
import type { GenerationJobRow } from "@/types/workspace"

export const assetPreviewBusyImageClass = "opacity-40"

export const assetPreviewBusyOverlayClass = "pointer-events-none absolute inset-0 z-10 bg-ui-surface-sunken/60"

export const assetPreviewBusySpinnerClass =
  "pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-transparent"

export function AssetPreviewBusyDim() {
  return <span className={assetPreviewBusyOverlayClass} aria-hidden />
}

type AssetPreviewBusySpinnerProps = {
  className?: string
}

export function AssetPreviewBusySpinner({ className }: AssetPreviewBusySpinnerProps) {
  return (
    <span className={cn(assetPreviewBusySpinnerClass, "text-ui-text-muted", className)} aria-busy="true">
      <Loader2 className="h-5 w-5 animate-spin" />
    </span>
  )
}

export type AssetPreviewGeneratingStatus = "queued" | "processing"

/**
 * Длительность завершения (вспышка сетки + проявление кадра из размытия), см. `.gen-motion` в application.css.
 * Появление (сгущение сетки) CSS проигрывает сам при монтировании.
 */
const GENERATING_EXIT_MS = 1150

type AssetPreviewGeneratingProps = {
  /** Идёт генерация. Держите компонент смонтированным: при `false` он сам проиграет завершение и исчезнет. */
  active: boolean
  status?: AssetPreviewGeneratingStatus
  /** Генерация поверх уже показанной картинки (новая версия): вуаль и чуть плотнее сетка. */
  onMedia?: boolean
  /** Стабильный ключ сущности — раскладка букв не меняется между перерисовками. */
  seedKey?: string
  /** Видимый масштаб (зум доски): на отдалении меньше пикселей канваса. */
  resolutionScale?: number
  /** Высота своей подписи статуса внизу превью (CSS px) — буквы обходят эту полосу. При `caption` считается сама. */
  reserveBottom?: number
  /**
   * Строка статуса снизу — конвенция для медиа-слотов (первая генерация и перегенерация).
   * Буквы её обходят, на завершении она прячется. Нижний оверлей (полоса ракурсов) рендерьте после компонента.
   */
  caption?: AssetPreviewGeneratingCaptionProps
  className?: string
}

/**
 * Анимация генерации на превью «Течение искр»: точечная сетка, течение и буквы FRAMERIVER.
 * Абсолютный слой на весь родитель (`relative`), без событий мыши; чипы держите выше `z-10`.
 */
export function AssetPreviewGenerating({
  active,
  status = "processing",
  onMedia = false,
  seedKey,
  resolutionScale = 1,
  reserveBottom = 0,
  caption,
  className,
}: AssetPreviewGeneratingProps) {
  const lettersReserve = caption
    ? ASSET_PREVIEW_GENERATING_CAPTION_RESERVE[caption.size ?? "tile"] + (caption.insetBottom ?? 0)
    : reserveBottom
  const [phase, setPhase] = useState<"idle" | "active" | "exit">(active ? "active" : "idle")
  const [prevActive, setPrevActive] = useState(active)
  if (active !== prevActive) {
    setPrevActive(active)
    setPhase(active ? "active" : phase === "idle" ? "idle" : "exit")
  }

  useEffect(() => {
    if (phase !== "exit") return
    const timer = window.setTimeout(() => setPhase("idle"), GENERATING_EXIT_MS)
    return () => window.clearTimeout(timer)
  }, [phase])

  if (phase === "idle") return null
  return (
    <>
      <span
        className={cn("gen-motion", className)}
        data-state={phase}
        data-on-media={onMedia ? "" : undefined}
        aria-hidden
      >
        {phase === "active" && onMedia ? <span className="gen-motion-soften" /> : null}
        {phase === "exit" ? <span className="gen-motion-reveal" /> : null}
        <GenerationMotionLayer
          status={status}
          onMedia={onMedia}
          seedKey={seedKey}
          resolutionScale={resolutionScale}
          reserveBottom={lettersReserve}
          finishing={phase === "exit"}
        />
      </span>
      {phase === "active" && caption ? (
        <AssetPreviewGeneratingCaption {...caption} className={cn("z-10", caption.className)} />
      ) : null}
    </>
  )
}

/** Высота строки статуса (CSS px) — передаётся в `AssetPreviewGenerating.reserveBottom`, чтобы буквы её обходили. */
export const ASSET_PREVIEW_GENERATING_CAPTION_RESERVE = { tile: 24, modal: 40 } as const

export type AssetPreviewGeneratingCaptionProps = {
  label: ReactNode
  /** Вторичный текст после статуса (этап, модель); обрезается по ширине. */
  detail?: ReactNode
  /** ISO-время старта — справа таймер. */
  startedAt?: string | null
  size?: "tile" | "modal"
  /** Нижний оверлей превью (полоса ракурсов): строка встаёт над ним, подложка тянется до края. */
  insetBottom?: number
  className?: string
}

/**
 * Строка статуса поверх `AssetPreviewGenerating`: во всю ширину снизу, на мягком градиенте от нижнего края.
 * Обычно через `AssetPreviewGenerating.caption`. Отдельно — когда статус живёт своей жизнью (тайл холста):
 * внутри `relative`-контейнера превью, а в `reserveBottom` анимации — высота строки (+ `insetBottom`).
 */
export function AssetPreviewGeneratingCaption({
  label,
  detail,
  startedAt,
  size = "tile",
  insetBottom = 0,
  className,
}: AssetPreviewGeneratingCaptionProps) {
  const isModal = size === "modal"
  return (
    <div
      className={cn("gen-motion-status pointer-events-none absolute inset-x-0 bottom-0 z-20", className)}
      style={insetBottom > 0 ? { paddingBottom: insetBottom } : undefined}
    >
      <div
        className={cn(
          "flex items-center",
          isModal ? "gap-2 px-4 pt-12 pb-3 text-sm" : "gap-1.5 px-2 pt-10 pb-1.5 text-[11px]",
        )}
      >
        <span className="shrink-0 font-medium text-ui-text-primary">{label}</span>
        <span className="min-w-0 flex-1 truncate text-ui-text-secondary">{detail ? <>· {detail}</> : null}</span>
        {startedAt ? (
          <JobElapsedTimer
            startedAt={startedAt}
            active
            className={cn("shrink-0 font-medium text-ui-text-primary", isModal ? "text-sm" : "text-[11px]")}
          />
        ) : null}
      </div>
    </div>
  )
}

/**
 * `status` + `caption` для `AssetPreviewGenerating` из задачи, которая даёт `busy`: статус и этап как в меню задач,
 * модель, таймер от старта задачи. Пока задача не дошла до стора — `fallbackLabel` без таймера.
 */
export function assetPreviewGeneratingFromJob(
  job: GenerationJobRow | null | undefined,
  {
    fallbackLabel,
    modelLabel,
    insetBottom,
  }: { fallbackLabel: ReactNode; modelLabel?: string | null; insetBottom?: number },
): { status: AssetPreviewGeneratingStatus; caption: AssetPreviewGeneratingCaptionProps } {
  const stage = job?.status_detail ? `${statusDetailLabel(job.status_detail)}…` : null
  return {
    status: job?.status === "queued" ? "queued" : "processing",
    caption: {
      label: job ? jobStatusLabel(job.status) : fallbackLabel,
      detail: [stage, modelLabel].filter(Boolean).join(" · ") || null,
      startedAt: jobStartedAt(job),
      insetBottom,
    },
  }
}

function GenerationMotionLayer({
  status,
  onMedia,
  seedKey,
  resolutionScale,
  reserveBottom,
  finishing,
}: {
  status: AssetPreviewGeneratingStatus
  onMedia: boolean
  seedKey?: string
  resolutionScale: number
  reserveBottom: number
  finishing: boolean
}) {
  const fallbackKey = useId()
  const key = seedKey ?? fallbackKey
  const hostRef = useRef<HTMLSpanElement>(null)
  const handleRef = useRef<GenerationMotionHandle | null>(null)
  const [initialOptions] = useState(() => ({ seedKey: key, status, onMedia, resolutionScale, reserveBottom }))

  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    const handle = registerGenerationMotion(host, initialOptions)
    handleRef.current = handle
    return () => {
      handle.dispose()
      handleRef.current = null
    }
  }, [initialOptions])

  useEffect(() => {
    handleRef.current?.update({ seedKey: key, status, onMedia, resolutionScale, reserveBottom })
  }, [key, status, onMedia, resolutionScale, reserveBottom])

  useEffect(() => {
    if (finishing) handleRef.current?.finish()
  }, [finishing])

  return <span ref={hostRef} className="gen-motion-layer" />
}
